import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import swal from 'sweetalert2'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons'

import {
  Button, ModalContext, SidebarModal,
} from '@campaignhub/suit-theme'

import { digObject } from '@campaignhub/javascript-utils'
import { useSetState } from '@campaignhub/react-hooks'

import ClientDetails from '@components/ClientDetails'

import { useClientForm } from '@hooks/useClientV3'
import useReduxAction from '@hooks/useReduxAction'

const defaultState = {
  customErrors: [],
}

const confirmDelete = (props, deleteFn) => {
  const { callbacks: { deleteClient } } = props
  swal
    .fire({
      title: 'Delete Client?',
      text: 'Are you sure? ',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it.',
      confirmButtonColor: '#DD6B55',
    })
    .then(({ value }) => {
      if (value){
        deleteClient(deleteFn)
      }
    })
}

const update = (props, clientParams, updateFn) => {
  const { callbacks: { updateClient } } = props
  updateClient(clientParams, updateFn)
}

const EditClientModal = (props) => {
  const [state, setState] = useSetState(defaultState)
  const { customErrors } = state
  const { callbacks, modalKey, showModal } = props

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, `${modalKey}`, {})
  const {
    callbacks: { deleteClient: deleteFn, updateClient: updateFn },
    client,
  } = modalPayload

  const options = {
    clientId: client.id,
    size: 0,
  }

  useReduxAction('usersV3', 'loadBlacklistedProvidersV3', options, [client.id])

  const { updating } = useSelector(reduxState => reduxState.clientsV3)

  const clientFormPayload = useClientForm(client)
  const { entityState } = clientFormPayload

  const save = () => {
    if (Object.keys(customErrors).length > 0){
      toast.warn('One or more fields did not match the validation.')
      return
    }
    update(props, entityState, updateFn)
  }

  return (
    <SidebarModal callbacks={callbacks} modalKey={modalKey} showModal={showModal} size="large">
      <SidebarModal.Header callbacks={callbacks} title="Edit" titleSecondLine="Client" />
      <SidebarModal.Content>
        <ClientDetails
          clientForm={clientFormPayload}
          customErrors={customErrors}
          callback={{ setCustomErrors: val => setState({ customErrors: val }) }}
        />
      </SidebarModal.Content>
      <SidebarModal.Footer>
        <Button
          buttonStyle="secondaryUtility"
          onClick={() => confirmDelete(props, deleteFn)}
          icon={<FontAwesomeIcon icon={faTrashAlt} />}
          size="medium"
          style={{ marginRight: 4, width: 'calc(50% - 4px)' }}
        >
          Delete Client
        </Button>
        <Button
          buttonStyle="primaryCreate"
          loading={updating}
          onClick={() => save()}
          size="medium"
          style={{ marginRight: 4, width: 'calc(50% - 4px)' }}
        >
          Save
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

EditClientModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

EditClientModal.defaultProps = {
  modalKey: 'EditClientModal',
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <EditClientModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
